import { useState, useEffect } from "react";
import { X, History, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { format, subDays } from "date-fns";
import { ptBR } from "date-fns/locale";
import { supabase } from "@/lib/supabase";

interface RegistroUsoFormProps {
  produto: any;
  agendamentos: any[];
  onSubmit: (data: any) => void;
  onCancel: () => void;
}

export function RegistroUsoForm({ produto, agendamentos, onSubmit, onCancel }: RegistroUsoFormProps) {
  const [formData, setFormData] = useState({
    agendamento_id: "",
    quantidade_usada: "",
    nivel_apos_uso: produto.nivel_atual || "Alto",
    observacoes: "",
  });

  const [historico, setHistorico] = useState<any[]>([]);

  useEffect(() => {
    const fetchHistorico = async () => {
      const desde = subDays(new Date(), 30).toISOString();
      const { data, error } = await supabase
        .from("estoque_uso")
        .select("id, created_at, quantidade_usada, nivel_apos_uso, observacoes")
        .eq("produto_id", produto.id)
        .gte("created_at", desde)
        .order("created_at", { ascending: false })
        .limit(20);
      if (error) {
        console.error("Erro ao buscar histórico de uso:", error);
        return;
      }
      setHistorico(
        (data || []).map((h) => ({
          id: h.id,
          data: h.created_at,
          qtd: h.quantidade_usada,
          nivel: h.nivel_apos_uso,
          obs: h.observacoes || "Uso registrado",
        }))
      );
    };
    fetchHistorico();
  }, [produto.id]);

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({ ...formData, produto_id: produto.id });
  };

  return (
    <div className="bg-card text-foreground p-6 rounded-lg border border-border w-full max-w-lg mx-auto shadow-sm animate-in zoom-in-95 duration-200 overflow-y-auto max-h-[90vh] custom-scrollbar">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-semibold">Registrar Uso</h2>
          <p className="text-sm text-muted-foreground">{produto.nome}</p>
        </div>
        <Button variant="ghost" size="icon" onClick={onCancel} className="text-muted-foreground hover:text-foreground hover:bg-muted">
          <X size={24} />
        </Button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5 mb-8">
        <div className="space-y-2">
          <Label className="text-muted-foreground text-sm font-medium">Agendamento Vinculado (Opcional)</Label>
          <Select value={formData.agendamento_id} onValueChange={(v) => handleChange("agendamento_id", v)}>
            <SelectTrigger className="bg-background border-border h-11 text-foreground">
              <SelectValue placeholder="Selecione um agendamento do dia" />
            </SelectTrigger>
            <SelectContent className="bg-card border-border text-foreground">
              {agendamentos.map(a => (
                <SelectItem key={a.id} value={a.id} className="focus:bg-primary focus:text-white">
                  {a.horario_inicio?.slice(0, 5)} - {a.cliente_nome || a.titulo}
                </SelectItem>
              ))}
              {agendamentos.length === 0 && (
                <div className="p-2 text-center text-xs text-muted-foreground">Nenhum agendamento hoje</div>
              )}
            </SelectContent>
          </Select>
        </div>

        {produto.tipo_controle === "Por unidade" ? (
          <div className="space-y-2">
            <Label className="text-muted-foreground text-sm font-medium">Quantidade Usada</Label>
            <Input 
              type="number"
              value={formData.quantidade_usada}
              onChange={(e) => handleChange("quantidade_usada", e.target.value)}
              placeholder="0"
              className="bg-background border-border h-11 text-foreground"
              required
            />
          </div>
        ) : (
          <div className="space-y-2">
            <Label className="text-muted-foreground text-sm font-medium">Novo Nível</Label>
            <Select value={formData.nivel_apos_uso} onValueChange={(v) => handleChange("nivel_apos_uso", v)}>
              <SelectTrigger className="bg-background border-border h-11 text-foreground">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-card border-border text-foreground">
                {["Alto", "Médio", "Baixo", "Crítico"].map(n => (
                  <SelectItem key={n} value={n} className="focus:bg-primary focus:text-white">{n}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <div className="space-y-2">
          <Label className="text-muted-foreground text-sm font-medium">Observações</Label>
          <Textarea 
            value={formData.observacoes}
            onChange={(e) => handleChange("observacoes", e.target.value)}
            placeholder="Ex: Usado na tattoo do braço..."
            className="bg-background border-border min-h-[80px] resize-none text-foreground"
          />
        </div>

        <Button type="submit" className="w-full bg-primary hover:bg-primary/90 text-white font-semibold h-12 shadow-sm">
          Confirmar Uso
        </Button>
      </form>

      <div className="space-y-4">
        <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-widest flex items-center gap-2">
          <History size={16} />
          Últimos 30 dias
        </h3>
        <div className="space-y-2">
          {historico.length === 0 ? (
            <p className="text-xs text-muted-foreground italic text-center py-4">Nenhum uso registrado nos últimos 30 dias</p>
          ) : historico.map(h => (
            <div key={h.id} className="p-3 rounded-lg bg-background border border-border flex justify-between items-center text-sm shadow-sm">
              <div>
                <p className="font-semibold text-foreground">{h.obs}</p>
                <p className="text-[10px] text-muted-foreground">{format(new Date(h.data), "dd 'de' MMMM", { locale: ptBR })}</p>
              </div>
              <div className="text-right">
                <p className="font-semibold text-primary">{h.qtd ? `-${h.qtd}` : h.nivel}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
